<template>
  <div class="page-shell">
    <div class="topbar">
      <div class="page-title"><h1>📋 扶持政策管理</h1><p>管理前台首页"扶持政策"模块的展示内容</p></div>
      <button class="btn btn-primary" :disabled="loading || submitting" @click="openPolicyDialog()">+ 添加扶持政策</button>
    </div>

    <div class="card">
      <div class="card-header"><h3>当前扶持政策列表（共{{ dataSource.policies.length }}条）</h3></div>
      <div class="policy-grid">
        <div v-for="(item, index) in dataSource.policies" :key="item.id" class="policy-card">
          <div class="pc-icon" @click="triggerPolicyImageUpload(item.id)">
            <img v-if="item.icon" :src="item.icon" :alt="item.title" referrerpolicy="no-referrer">
            <span v-else class="pc-icon-placeholder">🖼️</span>
            <span class="pc-icon-hint">点击替换</span>
          </div>
          <div class="pc-title">{{ item.title }}</div>
          <div class="pc-meta">排序值 {{ item.sort }}</div>
          <div class="pc-desc">{{ item.description }}</div>
          <div class="pc-actions">
            <button class="btn btn-sm btn-outline" :disabled="submitting" @click="openPolicyDialog(item)">编辑</button>
            <button class="btn btn-sm btn-danger" :disabled="submitting" @click="removePolicy(item.id)">删除</button>
            <button v-if="index > 0" class="btn btn-sm" style="background:var(--cog-border-light)" :disabled="submitting" @click="movePolicy(item.id, 'up')">↑</button>
            <button v-if="index < dataSource.policies.length - 1" class="btn btn-sm" style="background:var(--cog-border-light)" :disabled="submitting" @click="movePolicy(item.id, 'down')">↓</button>
          </div>
        </div>
      </div>
    </div>

    <el-dialog :title="policyForm.id ? '编辑扶持政策' : '添加扶持政策'" :visible.sync="dialogVisible">
      <div class="two-col-grid dialog-grid">
        <div class="form-group"><label>政策标题 *</label><input v-model="policyForm.title" type="text"></div>
        <div class="form-group"><label>排序值</label><input v-model.number="policyForm.sort" type="number" min="0"></div>
      </div>
      <div class="form-group"><label>政策描述 *</label><textarea v-model="policyForm.description" rows="4"></textarea></div>
      <div class="form-group"><label>图标 URL</label><input v-model="policyForm.icon" type="text"></div>
      <div class="form-group"><label>或上传本地图片</label><input type="file" accept="image/*" @change="handleDialogImageUpload"></div>
      <div v-if="uploading" class="upload-hint">图片上传中...</div>
      <div v-if="policyForm.icon" class="dialog-preview"><img :src="policyForm.icon" :alt="policyForm.title || '政策图片预览'" referrerpolicy="no-referrer"></div>
      <div slot="footer"><el-button @click="dialogVisible=false">取消</el-button><el-button type="primary" :loading="submitting" :disabled="uploading" @click="savePolicy">{{ policyForm.id ? '确认保存' : '确认添加' }}</el-button></div>
    </el-dialog>

    <input ref="policyImageInput" class="hidden-file-input" type="file" accept="image/*" @change="handleCardImageUpload">
  </div>
</template>

<script>
import {
  bannerUpload,
  policyCreate,
  policyDelete,
  policyDetail,
  policyList,
  policyMove,
  policyUpdate
} from '../../api/userMG'

const emptyPolicy = () => ({ id: null, icon: '', title: '', description: '', sort: 0 })

export default {
  name: 'home-policies',
  data() {
    return {
      dataSource: {
        policies: []
      },
      dialogVisible: false,
      policyForm: emptyPolicy(),
      replacePolicyId: null,
      loading: false,
      submitting: false,
      uploading: false
    }
  },
  created() {
    this.loadPolicies()
  },
  methods: {
    async loadPolicies() {
      this.loading = true

      try {
        const res = await policyList({ per_page: 100 })

        if (!this.isListSuccess(res)) {
          this.$message.error(this.getErrorMessage(res, '扶持政策列表加载失败'))
          return
        }

        this.dataSource.policies = res.data.map(item => this.normalizePolicy(item))
      } catch (error) {
        this.$message.error(this.getErrorMessage(error, '扶持政策列表加载失败'))
      } finally {
        this.loading = false
      }
    },
    async openPolicyDialog(item) {
      if (!item || !item.id) {
        this.policyForm = emptyPolicy()
        this.dialogVisible = true
        return
      }

      try {
        const res = await policyDetail(item.id)
        const detail = this.extractDetail(res)

        if (!detail || !detail.id) {
          this.$message.error(this.getErrorMessage(res, '扶持政策详情加载失败'))
          return
        }

        this.policyForm = this.normalizePolicy(detail)
      } catch (error) {
        this.$message.error(this.getErrorMessage(error, '扶持政策详情加载失败'))
        return
      }

      this.dialogVisible = true
    },
    async handleDialogImageUpload(event) {
      const file = event.target.files[0]
      if (!file) return

      const formData = new FormData()
      formData.append('file', file)
      this.uploading = true

      try {
        const res = await bannerUpload(formData)

        if (this.isRequestFailed(res)) {
          this.$message.error(this.getErrorMessage(res, '图片上传失败'))
          return
        }

        const imageUrl = this.extractUploadUrl(res)

        if (!imageUrl) {
          this.$message.error('上传成功，但未获取到图片地址')
          return
        }

        this.policyForm.icon = imageUrl
        this.$message.success('图片上传成功')
      } catch (error) {
        this.$message.error(this.getErrorMessage(error, '图片上传失败'))
      } finally {
        this.uploading = false
        event.target.value = ''
      }
    },
    triggerPolicyImageUpload(id) {
      if (this.submitting) return
      this.replacePolicyId = id
      this.$refs.policyImageInput.click()
    },
    async handleCardImageUpload(event) {
      const file = event.target.files[0]
      if (!file) return

      const policy = this.dataSource.policies.find(item => item.id === this.replacePolicyId)
      if (!policy) {
        event.target.value = ''
        return
      }

      const formData = new FormData()
      formData.append('file', file)
      this.uploading = true
      this.submitting = true

      try {
        const uploadRes = await bannerUpload(formData)

        if (this.isRequestFailed(uploadRes)) {
          this.$message.error(this.getErrorMessage(uploadRes, '图片上传失败'))
          return
        }

        const imageUrl = this.extractUploadUrl(uploadRes)

        if (!imageUrl) {
          this.$message.error('上传成功，但未获取到图片地址')
          return
        }

        const res = await policyUpdate(policy.id, {
          icon: imageUrl,
          title: policy.title,
          description: policy.description,
          sort: policy.sort
        })

        if (this.isMutationFailed(res)) {
          this.$message.error(this.getErrorMessage(res, '政策图片更新失败'))
          return
        }

        this.$message.success('政策图片已替换！')
        await this.loadPolicies()
      } catch (error) {
        this.$message.error(this.getErrorMessage(error, '政策图片更新失败'))
      } finally {
        this.uploading = false
        this.submitting = false
        event.target.value = ''
        this.replacePolicyId = null
      }
    },
    async savePolicy() {
      const payload = this.buildPolicyPayload()

      if (!payload.title || !payload.description) {
        this.$message.warning('请填写完整的政策信息')
        return
      }

      this.submitting = true

      try {
        const res = this.policyForm.id
          ? await policyUpdate(this.policyForm.id, payload)
          : await policyCreate(payload)

        if (this.isMutationFailed(res)) {
          this.$message.error(this.getErrorMessage(res, this.policyForm.id ? '扶持政策更新失败' : '扶持政策创建失败'))
          return
        }

        this.dialogVisible = false
        this.$message.success(this.policyForm.id ? '扶持政策已更新！' : '扶持政策已添加！')
        await this.loadPolicies()
      } catch (error) {
        this.$message.error(this.getErrorMessage(error, this.policyForm.id ? '扶持政策更新失败' : '扶持政策创建失败'))
      } finally {
        this.submitting = false
      }
    },
    async removePolicy(id) {
      this.submitting = true

      try {
        const res = await policyDelete(id)

        if (this.isMutationFailed(res) && res !== 204) {
          this.$message.error(this.getErrorMessage(res, '扶持政策删除失败'))
          return
        }

        this.$message.success('扶持政策已删除！')
        await this.loadPolicies()
      } catch (error) {
        this.$message.error(this.getErrorMessage(error, '扶持政策删除失败'))
      } finally {
        this.submitting = false
      }
    },
    async movePolicy(id, direction) {
      this.submitting = true

      try {
        const res = await policyMove(id, { direction })

        if (this.isMutationFailed(res) && res !== 204) {
          this.$message.error(this.getErrorMessage(res, '扶持政策排序失败'))
          return
        }

        await this.loadPolicies()
      } catch (error) {
        this.$message.error(this.getErrorMessage(error, '扶持政策排序失败'))
      } finally {
        this.submitting = false
      }
    },
    normalizePolicy(item) {
      return {
        id: item.id,
        icon: item.icon || '',
        title: item.title || '',
        description: item.description || '',
        sort: item.sort === undefined || item.sort === null || item.sort === '' ? 0 : Number(item.sort)
      }
    },
    buildPolicyPayload() {
      return {
        icon: (this.policyForm.icon || '').trim(),
        title: (this.policyForm.title || '').trim(),
        description: (this.policyForm.description || '').trim(),
        sort: this.policyForm.sort === undefined || this.policyForm.sort === null || this.policyForm.sort === '' ? 0 : Number(this.policyForm.sort)
      }
    },
    extractDetail(res) {
      if (!res || typeof res !== 'object' || res.message || res.detail || res.errors) {
        return null
      }

      if (res.data && !Array.isArray(res.data) && typeof res.data === 'object') {
        return res.data
      }

      return res
    },
    extractUploadUrl(res) {
      const payload = this.extractDetail(res)

      if (!payload || typeof payload !== 'object') {
        return ''
      }

      return payload.url || payload.image_url || payload.path || payload.file_url || ''
    },
    isListSuccess(res) {
      return !!(res && typeof res === 'object' && Array.isArray(res.data) && !res.message && !res.detail && !res.errors)
    },
    isRequestFailed(res) {
      return typeof res === 'string' || !!(res && typeof res === 'object' && (res.message || res.detail || res.errors))
    },
    isMutationFailed(res) {
      return typeof res === 'string' || !!(res && typeof res === 'object' && (res.detail || res.errors))
    },
    getErrorMessage(error, fallback) {
      if (!error) return fallback
      if (typeof error === 'string') return error
      if (error.message) return error.message
      if (error.detail) return error.detail
      if (error.errors) {
        const firstKey = Object.keys(error.errors)[0]
        const firstError = firstKey ? error.errors[firstKey] : ''

        if (Array.isArray(firstError)) {
          return firstError[0] || fallback
        }

        return firstError || fallback
      }

      return fallback
    }
  }
}
</script>

<style scoped>
.dialog-grid,
.form-group {
  margin-bottom: 14px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  min-height: 40px;
  padding: 10px 12px;
  border: 1.5px solid var(--cog-border);
  border-radius: var(--cog-radius-sm);
}

.form-group textarea {
  resize: vertical;
}

.upload-hint {
  margin-top: 8px;
  font-size: 12px;
  color: var(--cog-text-secondary);
}

.pc-icon {
  position: relative;
  cursor: pointer;
}

.pc-icon img {
  display: block;
  width: 72px;
  height: 72px;
  margin: 0 auto;
  object-fit: cover;
  border-radius: 16px;
  border: 1px solid var(--cog-border);
}

.pc-icon-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  margin: 0 auto;
  font-size: 28px;
  color: var(--cog-text-muted);
  border: 1px dashed var(--cog-border);
  border-radius: 16px;
}

.pc-icon-hint {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  padding: 2px 0;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 11px;
  opacity: 0;
  transition: opacity var(--cog-transition);
}

.pc-icon:hover .pc-icon-hint {
  opacity: 1;
}

.pc-meta {
  margin-top: 6px;
  font-size: 12px;
  color: var(--cog-text-secondary);
}

.pc-actions {
  margin-top: 10px;
}

.dialog-preview {
  margin-top: 12px;
}

.dialog-preview img {
  display: block;
  max-width: 100%;
  max-height: 200px;
  border: 1px solid var(--cog-border);
  border-radius: var(--cog-radius);
}

.hidden-file-input {
  display: none;
}
</style>



// WEBPACK FOOTER //
// src/views/home/policies.vue